import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import { addDays, format } from "date-fns";
import { toast } from "react-toastify";

import { appointmentsApi } from "../Components/appointmentsApi";
import { formatDateTime } from "../Components/appointmentsUiUtils";
import styles from "./PublicBookingPage.module.scss";

export default function PublicBookingPage() {
  const { token } = useParams();
  const [bookingLink, setBookingLink] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });

  const range = useMemo(
    () => ({
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: format(addDays(new Date(), 14), "yyyy-MM-dd"),
    }),
    []
  );

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      const data = await appointmentsApi.validateBookingLink(token);
      if (!data?.valid) {
        setError(data?.error || "Invalid booking link");
        setLoading(false);
        return;
      }
      setBookingLink(data.booking_link);
      setLoading(false);
    };
    run();
  }, [token]);

  useEffect(() => {
    const loadSlots = async () => {
      if (!bookingLink?.counsellor_id) {
        return;
      }
      const data = await appointmentsApi.getAvailability({
        counsellor_id: bookingLink.counsellor_id,
        start_date: range.start_date,
        end_date: range.end_date,
        timezone: form.timezone,
        appointment_type_id: bookingLink.appointment_type_id || undefined,
      });
      setSlots(data?.slots || []);
    };
    loadSlots();
  }, [bookingLink, range.start_date, range.end_date, form.timezone]);

  const submitBooking = async () => {
    if (!selectedSlot) {
      toast.error("Select an available slot.");
      return;
    }
    const payload = {
      ...form,
      start_time: selectedSlot.start_time_utc,
      appointment_type_id: bookingLink.appointment_type_id,
    };
    const data = await appointmentsApi.publicBook(token, payload);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success("Booking confirmed. A confirmation email has been sent.");
  };

  if (loading) {
    return <div className={styles.loading}>Loading booking page...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <Container fluid className={styles.page}>
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className={styles.card}>
            <Card.Body>
              <h4 className={styles.title}>Book an Appointment</h4>
              <p className={styles.subtitle}>
                Choose a slot, enter your details, and confirm your appointment.
              </p>

              <Row className={`g-2 ${styles.formRow}`}>
                <Col md={4}>
                  <Form.Control
                    placeholder="Full name"
                    value={form.customer_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    type="email"
                    placeholder="Email"
                    value={form.customer_email}
                    onChange={(e) => setForm((prev) => ({ ...prev, customer_email: e.target.value }))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    placeholder="Phone (optional)"
                    value={form.customer_phone}
                    onChange={(e) => setForm((prev) => ({ ...prev, customer_phone: e.target.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Form.Control
                    value={form.timezone}
                    onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
                  />
                </Col>
              </Row>

              <Table responsive hover>
                <thead>
                  <tr>
                    <th></th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Capacity</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.length === 0 ? (
                    <tr>
                      <td colSpan={4}>No slots available for this period.</td>
                    </tr>
                  ) : (
                    slots.slice(0, 120).map((slot) => (
                      <tr key={`${slot.start_time_utc}-${slot.counsellor_id}`}>
                        <td>
                          <Form.Check
                            type="radio"
                            name="slot"
                            checked={selectedSlot?.start_time_utc === slot.start_time_utc}
                            onChange={() => setSelectedSlot(slot)}
                          />
                        </td>
                        <td>{formatDateTime(slot.start_time_utc, form.timezone)}</td>
                        <td>{formatDateTime(slot.end_time_utc, form.timezone)}</td>
                        <td>{slot.available_capacity}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              <div className={styles.actions}>
                <Button onClick={submitBooking}>Confirm Booking</Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
